import { Component } from "@theme/component";
import {
  fetchConfig,
  preloadImage,
  onAnimationEnd,
  yieldToMainThread,
} from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId)
      );
    }
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest("form");
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (
      this.closest("product-form-component")
    );
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== "true") {
      const animationEnabled = this.dataset.addToCartAnimation === "true";
      if (animationEnabled && !event.target.closest(".quick-add-modal")) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    let flyToCartClass = addToCartButton.classList.contains("quick-add__button")
      ? "fly-to-cart--quick"
      : "fly-to-cart--main";

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.style.setProperty("--start-opacity", "0");
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId)
    );
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== "true") {
      addToCartButton.dataset.added = "true";
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute("data-added");

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find(
      (item) => item.variant_id.toString() === variantIdInput.value.toString()
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (
      this.querySelector("quantity-selector-component")
    );
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error("Failed to fetch cart quantity:", error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source === "product-form-component"
    )
      return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {SubmitEvent} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    
    // Check if this is a createCustomer form submission and ignore it
    const submitter = /** @type {HTMLElement | null} */ (event.submitter);
    const target = /** @type {HTMLElement | null} */ (event.target);
    
    if (
      submitter?.classList.contains('js-create-customer') ||
      target?.id === 'create_customer' ||
      submitter?.id === 'signupButton'
    ) {
      // This is the createCustomer form submission, not add to cart
      return;
    }
    
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll("add-to-cart-component")
      );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || "";
        const errorMessage = errorTemplate.replace(
          "{{ maximum }}",
          validation.maxQuantity?.toString() || ""
        );
        if (addToCartTextError) {
          addToCartTextError.classList.remove("hidden");

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const selectedSize = /** @type {HTMLInputElement | null} */ (
      document.getElementById("selected-size")
    );

    let formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component"
    );
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    });

    if (
      selectedSize &&
      selectedSize.classList.contains("standard-size") &&
      selectedSize.value
    ) {
      formData = this.#handleStandardSizeIds(formData, selectedSize.value);
    } else {
      formData = this.#handleCustomSizes(formData);
    }

    const fetchCfg = fetchConfig("javascript", { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "text/html",
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute("id") || "",
              response.message,
              response.description,
              response.errors
            )
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove("hidden");

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get("id");

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          if (!id) throw new Error("Form ID is required");

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton =
            allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector(
              ".add-to-cart-text--added"
            );
            const addedText =
              addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle("hidden", cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null
  ) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement(
        "beforebegin",
        /** @type {Element} */ (newElement.cloneNode(true))
      );
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? "";

    // Update bundle pack metafield inputs when variant changes
    this.#updateBundlePackInputs(event.detail.data.html);
    const {
      addToCartButtonContainer: currentAddToCartButtonContainer,
      acceleratedCheckoutButtonContainer,
    } = this.refs;
    const currentAddToCartButton =
      currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (
      !currentAddToCartButtonContainer ||
      (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)
    )
      return;

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      currentAddToCartButtonContainer.disable();
    } else {
      // Check if we're in an "only standard color" situation before enabling
      const onlyStandardColorInput = /** @type {HTMLInputElement | null} */ (
        document.getElementById("check_if_standard_color_selected")
      );
      const isOnlyStandardColorSelected =
        onlyStandardColorInput?.value === "true";

      // Check if product has standard sizes and no size is selected yet
      const hasCustomer = window.globals?.customer?.id;
      const hasStandardSizes = window.globals?.product?.tags?.includes(
        "have_standard_sizes"
      );
      const selectedSizeInput = /** @type {HTMLInputElement | null} */ (
        document.getElementById("selected-size")
      );
      const hasSizeSelected = selectedSizeInput?.value;

      // Also restrict logged-in customers without a quiz/fit on standard-size products
      const hasQuizOrFit = hasCustomer && (
        window.globals?.latestQuizType ||
        window.globals?.customer?.tags?.includes("smartfit") ||
        window.globals?.customer?.tags?.includes("bodymeasurements")
      );
      const isStandardSizesRestricted =
        hasStandardSizes && !hasSizeSelected && (!hasCustomer || !hasQuizOrFit);

      // For bundle products, always require size re-selection after variant change
      const isBundleProduct = !!document.getElementById("bundle_product_variants");
      const isBundleSizeRequired = isBundleProduct && !hasSizeSelected;

      if (!isOnlyStandardColorSelected && !isStandardSizesRestricted && !isBundleSizeRequired) {
        currentAddToCartButtonContainer.enable();
      }
      // If it's an only standard color, keep button disabled (Vue components will handle the state)
    }

    // Store the current button text if it's an only-standard-color situation
    const onlyStandardColorInput = /** @type {HTMLInputElement | null} */ (
      document.getElementById("check_if_standard_color_selected")
    );
    const isOnlyStandardColorSelected =
      onlyStandardColorInput?.value === "true";
    let preservedButtonText = null;

    if (isOnlyStandardColorSelected && currentAddToCartButton) {
      const buttonTextElement =
        currentAddToCartButton.querySelector(".AddToCartText");
      if (buttonTextElement) {
        preservedButtonText = buttonTextElement.textContent;
      }
    }

    const newAddToCartButton = event.detail.data.html.querySelector(
      'product-form-component [ref="addToCartButton"]'
    );
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);

      // Restore the preserved button text if needed
      if (preservedButtonText && isOnlyStandardColorSelected) {
        const buttonTextElement =
          currentAddToCartButton.querySelector(".AddToCartText");
        if (buttonTextElement) {
          buttonTextElement.textContent = preservedButtonText;
        }
      }
    }

    if (acceleratedCheckoutButtonContainer) {
      if (
        event.detail.resource == null ||
        event.detail.resource.available == false
      ) {
        acceleratedCheckoutButtonContainer?.setAttribute("hidden", "true");
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100"
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector(
        'quantity-selector-component input[ref="quantityInput"]'
      )
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    const newQuantityRules =
      event.detail.data.html.querySelector(".quantity-rules");
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem =
      event.detail.data.html.querySelector("price-per-item");
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if (
      (isQuantityRulesChanging || isPricePerItemChanging) &&
      quantitySelector
    ) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector(
        ".product-form-buttons"
      );

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector(
            'quantity-selector-component input[ref="quantityInput"]'
          )
        );

        if (
          this.refs.quantitySelector?.updateConstraints &&
          newQuantityInputElement &&
          currentQuantityValue
        ) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        [".quantity-label", quantityLabel, quantitySelector],
        [".quantity-rules", quantityRules, this.refs.productFormButtons],
        ["price-per-item", pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(
          currentElement,
          event.detail.data.html.querySelector(selector),
          fallback
        );
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing =
      event.detail.data.html.querySelector("volume-pricing");
    this.#morphOrUpdateElement(
      currentVolumePricing,
      newVolumePricing,
      this.refs.productFormButtons
    );

    const hasB2BFeatures =
      quantityRules ||
      newQuantityRules ||
      pricePerItem ||
      newPricePerItem ||
      currentVolumePricing ||
      newVolumePricing;

    // Update select-fit button visibility based on variant availability
    this.#updateSelectFitButtonVisibility(event.detail.resource);

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };

  /**
   * Updates the select-fit button visibility based on variant availability
   * @param {object | null | undefined} variant - The current variant
   */
  #updateSelectFitButtonVisibility(variant) {
    const selectFitButton = document.getElementById("select-fit-button");
    const fitSelectionBlock = document.querySelector(".fit-selection-block");
    const formContentQuiz = document.querySelector(
      ".product-form__content__quiz"
    );

    if (!selectFitButton) return;

    const hasStandardSizes = window.globals?.product?.tags?.includes(
      "have_standard_sizes"
    );
    if (!hasStandardSizes) return;

    // Check if variant is available
    const isVariantAvailable = variant && variant.available === true;

    if (isVariantAvailable) {
      // Show the button if variant is available and product has standard sizes
      selectFitButton.style.display = "";
      fitSelectionBlock?.classList.add("mb-8");
      formContentQuiz?.classList.remove("hidden");
    } else {
      // Hide the button if variant is sold out
      selectFitButton.style.display = "none";
      fitSelectionBlock?.classList.remove("mb-8");
      formContentQuiz?.classList.add("hidden");
    }
  }

  /**
   * Handles standard size IDs - supports both single ID and array of IDs
   * @param {FormData} formData - The form data to modify
   * @param {string} sizeValue - The selected size value (can be single ID or JSON array)
   * @returns {FormData} The modified form data
   */
  #handleStandardSizeIds(formData, sizeValue) {
    formData = this.#deleteLineItemProps(formData);
    formData.delete("id");
    const bundle_pack = /** @type {HTMLInputElement | null} */ (
      document.getElementById("bundle_product_variants")
    );
    let quantity = "1";
    try {
      const parsedValue = JSON.parse(sizeValue);

      // Check if it's an array of IDs (multiple products)
      if (Array.isArray(parsedValue)) {
        if (bundle_pack && bundle_pack.value) {
          quantity =
            bundle_pack.getAttribute("data-bundle-variant-quantity") || "1";
        } else {
          const quantityValue = formData.get("quantity");
          quantity =
            (typeof quantityValue === "string" ? quantityValue : null) || "1";
        }

        // For multiple products, we need to use items[] format
        parsedValue.forEach((variantId, index) => {
          formData.append(`items[${index}][id]`, variantId.toString());
          formData.append(`items[${index}][quantity]`, quantity);
        });
      } else {
        // Single variant ID
        formData.append("id", parsedValue.toString());
      }
    } catch (e) {
      // Not JSON, treat as single ID string
      formData.append("id", sizeValue);
    }

    return formData;
  }

  /**
   * Updates bundle pack metafield inputs when variant changes
   * @param {Document | Element} html - The new HTML from variant update
   */
  #updateBundlePackInputs(html) {
    // Update bundle_products_variant input for bundle_pack products
    const currentBundlePackInput = /** @type {HTMLInputElement | null} */ (
      document.querySelector(".bundle_products_variant")
    );
    const newBundlePackInput = /** @type {HTMLInputElement | null} */ (
      html.querySelector(".bundle_products_variant")
    );

    if (currentBundlePackInput && newBundlePackInput) {
      currentBundlePackInput.dataset.shirtId =
        newBundlePackInput.dataset.shirtId || "";
      currentBundlePackInput.dataset.tShirtId =
        newBundlePackInput.dataset.tShirtId || "";
      currentBundlePackInput.dataset.bundleSuit =
        newBundlePackInput.dataset.bundleSuit || "";
      currentBundlePackInput.dataset.otherBundleProducts =
        newBundlePackInput.dataset.otherBundleProducts || "";
      currentBundlePackInput.dataset.otherBundleProductsTypes =
        newBundlePackInput.dataset.otherBundleProductsTypes || "";
      currentBundlePackInput.value = newBundlePackInput.value || "";
    }

    // Sync bundle_product_2.0 hidden inputs from new HTML before reading them
    const hiddenInputIds = ["custom-product-variants", "variant-quantities", "standard_size_products"];
    let bundleInputsUpdated = false;
    for (const inputId of hiddenInputIds) {
      const currentInput = /** @type {HTMLInputElement | null} */ (
        document.getElementById(inputId)
      );
      const newInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector(`#${inputId}`)
      );
      if (currentInput && newInput) {
        currentInput.value = newInput.value;
        bundleInputsUpdated = true;
      }
    }

    if (bundleInputsUpdated) {
      document.dispatchEvent(new CustomEvent("bundle-product-inputs-updated"));
    }

    // Update bundle_product_variants input for bundle_product products
    const currentBundleProductInput = /** @type {HTMLInputElement | null} */ (
      document.getElementById("bundle_product_variants")
    );
    const newBundleProductInput = /** @type {HTMLInputElement | null} */ (
      html.querySelector("#bundle_product_variants")
    );

    if (currentBundleProductInput && newBundleProductInput) {
      currentBundleProductInput.dataset.bundleVariants =
        newBundleProductInput.dataset.bundleVariants || "";
      currentBundleProductInput.dataset.bundleColors =
        newBundleProductInput.dataset.bundleColors || "";
      currentBundleProductInput.dataset.bundleVariantQuantity =
        newBundleProductInput.dataset.bundleVariantQuantity || "";
      currentBundleProductInput.value = newBundleProductInput.value || "";

      // For bundle_product_2.0: override quantity from variant-quantities hidden input
      const variantQtyInput = /** @type {HTMLInputElement | null} */ (
        document.getElementById("variant-quantities")
      );
      if (variantQtyInput && variantQtyInput.value) {
        currentBundleProductInput.dataset.bundleVariantQuantity =
          variantQtyInput.value;
      }
    }

    // Update multiple_bundle_products input
    const currentMultipleBundleInput = /** @type {HTMLInputElement | null} */ (
      document.getElementById("multiple_bundle_products")
    );
    const newMultipleBundleInput = /** @type {HTMLInputElement | null} */ (
      html.querySelector("#multiple_bundle_products")
    );

    if (currentMultipleBundleInput && newMultipleBundleInput) {
      currentMultipleBundleInput.dataset.bundleVariantQuantity =
        newMultipleBundleInput.dataset.bundleVariantQuantity || "";
      currentMultipleBundleInput.dataset.bundleProducts =
        newMultipleBundleInput.dataset.bundleProducts || "";
      currentMultipleBundleInput.value = newMultipleBundleInput.value || "";
    }
  }

  /**
   * Handles custom sizes
   * @param {FormData} formData - The form data to modify
   * @returns {FormData} The modified form data
   */
  #handleCustomSizes(formData) {
    // Implement the logic to handle custom sizes here
    // For example, you might want to add custom size properties to the formData
    const bundle_variant = /** @type {HTMLInputElement | null} */ (
      document.querySelector(".bundle_products_variant")
    );

    // Get bundle product variants input
    const bundle_product_variants = /** @type {HTMLInputElement | null} */ (
      document.getElementById("bundle_product_variants")
    );

    // Handle other bundle products data
    const otherBundleProducts = bundle_variant?.dataset.otherBundleProducts;
    const otherBundleProductsTypes =
      bundle_variant?.dataset.otherBundleProductsTypes;

    /** @type {{properties: {[key: string]: string}, [key: string]: any}} */
    var item = { properties: {} };

    formData.forEach(function (value, key) {
      if (key.includes("properties[")) {
        key = key.replace("properties[", "").replace("]", "");
        if (value && typeof value === "string") {
          item.properties[key] = value.trim();
        }
      } else {
        item[key] = value;
      }
    });

    // Check if bundle pack then add bundle pack property to item properties
    const bundlePackInput = /** @type {HTMLInputElement | null} */ (
      document.querySelector('input[name="properties[_bundle_pack]"]')
    );
    if (bundlePackInput?.value) {
      item.properties["_bundle_pack"] = bundlePackInput.value;
    }

    // Handle bundle_product_2.0 first: use custom-product-variants and variant-quantities hidden inputs
    const customProductVariants = /** @type {HTMLInputElement | null} */ (
      document.getElementById("custom-product-variants")
    );
    const variantQuantities = /** @type {HTMLInputElement | null} */ (
      document.getElementById("variant-quantities")
    );
    const customIds = customProductVariants?.value
      ? customProductVariants.value.split(",").filter((id) => id && id !== "")
      : [];

    if (customIds.length > 0) {
      formData.delete("id");
      const quantity = variantQuantities?.value || "1";

      const propertiesToCopy = [
        "Sizing Method",
        "fit",
        "length",
        "rise",
        "_estimation_date",
      ];

      for (let i = 0; i < customIds.length; i++) {
        formData.append(`items[${i}][id]`, /** @type {string} */ (customIds[i]));
        formData.append(`items[${i}][quantity]`, quantity);

        propertiesToCopy.forEach((prop) => {
          const value = formData.get(`properties[${prop}]`);
          if (value) {
            formData.append(
              `items[${i}][properties][${prop}]`,
              value.toString()
            );
          }
        });
      }
    } else if (bundle_variant || bundle_product_variants) {
      formData.delete("id");
      if (bundle_variant) {
        // Handle bundle products
        if (otherBundleProducts) {
          this.#handleOtherBundleProducts(
            formData,
            otherBundleProducts,
            otherBundleProductsTypes,
            item.properties
          );
        }
        if (bundle_variant.dataset.t_shirt_id) {
          formData.append("id", bundle_variant.dataset.t_shirt_id || "");
          formData.append("quantity", "1");
          formData.append(
            "properties[fit]",
            item.properties["t-shirt_size"] || ""
          );
          formData.append("properties[length]", "Standard");
          formData.append(
            "properties[Sizing Method]",
            item.properties["Sizing Method"] || ""
          );
          formData.append(
            "properties[_estimation_date]",
            item.properties["_estimation_date"] || ""
          );
          formData.append(
            "properties[_bundle_pack]",
            item.properties["_bundle_pack"] || ""
          );
        }
      } else if (bundle_product_variants) {
        formData = this.#handleBundles(formData, bundle_product_variants);
      }
    }

    return formData;
  }

  /**
   * @param {FormData} formData
   */

  #deleteLineItemProps(formData) {
    const data = formData;
    data.delete("properties[Sizing Method]");
    if (data.has("properties[fit]")) {
      data.delete("properties[fit]");
    }
    if (data.has("properties[length]")) {
      data.delete("properties[length]");
    }
    if (data.has("properties[rise]")) {
      data.delete("properties[rise]");
    }
    if (data.has("properties[blazer length]")) {
      data.delete("properties[blazer length]");
    }
    if (data.has("properties[shirt_size]")) {
      data.delete("properties[shirt_size]");
    }
    if (data.has("properties[pant style]")) {
      data.delete("properties[pant style]");
    }
    if (data.has("properties[inseam]")) {
      data.delete("properties[inseam]");
    }
    return data;
  }

  /**
   * @param {FormData} formData
   * @param {string} otherBundleProducts
   * @param {string} otherBundleProductsTypes
   * @param {{[key: string]: string}} properties
   */

  #handleOtherBundleProducts(
    formData,
    otherBundleProducts,
    otherBundleProductsTypes,
    properties = {}
  ) {
    var products = otherBundleProducts.slice(0, -1).split(",");
    const product_types = otherBundleProductsTypes.slice(0, -1).split(",");

    for (let index = 0; index < products.length; index++) {
      const element = products[index];
      const productType = product_types[index];

      if (element) {
        formData.append(`items[${index}][id]`, element);
        formData.append(`items[${index}][quantity]`, "1");

        // Skip properties for Accessory products
        if (productType === "Accessory") {
          continue;
        }

        // Add all properties for non-accessory items
        if (properties["_bundle_pack"]) {
          formData.append(
            `items[${index}][properties][_bundle_pack]`,
            properties["_bundle_pack"]
          );
        }
        if (properties["Sizing Method"]) {
          formData.append(
            `items[${index}][properties][Sizing Method]`,
            properties["Sizing Method"]
          );
        }
        if (properties["fit"]) {
          formData.append(
            `items[${index}][properties][fit]`,
            properties["fit"]
          );
        }
        if (properties["length"]) {
          formData.append(
            `items[${index}][properties][length]`,
            properties["length"]
          );
        }
        if (properties["pant_length"]) {
          formData.append(
            `items[${index}][properties][pant length]`,
            properties["pant_length"]
          );
        }
        if (properties["pant_rise"]) {
          formData.append(
            `items[${index}][properties][pant rise]`,
            properties["pant_rise"]
          );
        }
      }
    }
  }

  /**
   * @param {FormData} formData
   * @param {HTMLInputElement} bundle_product_variants
   * @returns {FormData} The modified form data
   */
  #handleBundles(formData, bundle_product_variants) {
    const products_ids = bundle_product_variants.getAttribute(
      "data-bundle-variants"
    );
    if (products_ids && products_ids !== "") {
      const product_quantity = bundle_product_variants.getAttribute(
        "data-bundle-variant-quantity"
      );
      const ids = products_ids.split(",").filter((id) => id && id !== "");

      // Extract properties from root level to apply to each bundle item
      const propertiesToCopy = [
        "Sizing Method",
        "fit",
        "length",
        "rise",
        "blazer length",
        "shirt_size",
        "pant style",
        "inseam",
        "pant length",
        "pant_length",
        "pant rise",
        "pant_rise",
      ];

      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        if (id) {
          formData.append(`items[${i}][id]`, id);
          formData.append(
            `items[${i}][quantity]`,
            product_quantity ? product_quantity : "1"
          );

          // Copy properties to each item
          propertiesToCopy.forEach((prop) => {
            const value = formData.get(`properties[${prop}]`);
            if (value) {
              formData.append(
                `items[${i}][properties][${prop}]`,
                value.toString()
              );
            }
          });
        }
      }
    }
    return formData;
  }
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}
